import { PROVIDER } from 'Pods/ThreeDStudio/Store/constants'

// Backend `action` values for the image runs the assistant drives - see
// Generator::Providers::Flux::Schemas::Catalog and
// Generator::Providers::NanoBanana::Schemas::Catalog. Flux declares all of them;
// nano-banana declares every one except inpaint.
export const IMAGE_ACTION = {
  TEXT_TO_IMAGE: 'text-to-image',
  SIMILAR_TO: 'similar-to',
  IMAGE_EDIT: 'image-edit',
  MULTI_IMAGE_EDIT: 'multi-image-edit',
  INPAINT: 'inpaint',
}

// Every image model, by provider and action. New runs do not pick from this table - they
// send the action and let the backend rank the providers - but a run that already exists
// only records the model it used, so this is what maps one back to its action.
//
// Both providers take aspect_ratio and resolution; flux additionally derives width/height
// from them. inpaint is flux-only: it takes 1 input image plus a separate mask asset.
const MODEL_ID_BY_PROVIDER = {
  [PROVIDER.FLUX]: {
    [IMAGE_ACTION.TEXT_TO_IMAGE]: 'bfl-text-to-image-flux2-pro',
    [IMAGE_ACTION.SIMILAR_TO]: 'bfl-similar-to-flux2-pro',
    [IMAGE_ACTION.IMAGE_EDIT]: 'bfl-image-edit-flux2-pro',
    [IMAGE_ACTION.MULTI_IMAGE_EDIT]: 'bfl-multi-image-edit-flux2-pro',
    [IMAGE_ACTION.INPAINT]: 'bfl-inpaint-flux1-fill',
  },
  [PROVIDER.NANO_BANANA]: {
    [IMAGE_ACTION.TEXT_TO_IMAGE]: 'gemini-text-to-image-nb2-lite',
    [IMAGE_ACTION.SIMILAR_TO]: 'gemini-similar-to-nb2-lite',
    [IMAGE_ACTION.IMAGE_EDIT]: 'gemini-image-edit-nb2-lite',
    [IMAGE_ACTION.MULTI_IMAGE_EDIT]: 'gemini-multi-image-edit-nb2-lite',
  },
}

// Old model ids from runs stored before the Sep-2026 rename. Kept so that imageActionOf
// still classifies historical generator_runs rows. New ids win on any collision.
const LEGACY_MODEL_ID_ACTIONS = {
  'flux-text-to-image-pro': IMAGE_ACTION.TEXT_TO_IMAGE,
  'flux-similar-image-pro': IMAGE_ACTION.SIMILAR_TO,
  'flux-image-edit-pro': IMAGE_ACTION.IMAGE_EDIT,
  'flux-multi-image-edit-pro': IMAGE_ACTION.MULTI_IMAGE_EDIT,
  'flux-masked-edit-pro': IMAGE_ACTION.INPAINT,
  'nano-banana-text-to-image-lite': IMAGE_ACTION.TEXT_TO_IMAGE,
  'nano-banana-similar-image-lite': IMAGE_ACTION.SIMILAR_TO,
  'nano-banana-image-edit-lite': IMAGE_ACTION.IMAGE_EDIT,
  'nano-banana-multi-image-edit-lite': IMAGE_ACTION.MULTI_IMAGE_EDIT,
  'nano-banana-masked-edit-lite': IMAGE_ACTION.INPAINT,
}

// Reverse lookup across every provider, so a run is classified by what it did rather than
// by which provider ran it - the backend is free to answer the same action with either.
const ACTION_BY_MODEL_ID = {
  ...LEGACY_MODEL_ID_ACTIONS,
  ...Object.fromEntries(
    Object.values(MODEL_ID_BY_PROVIDER).flatMap(
      actions => Object.entries(actions).map(([action, modelId]) => [modelId, action]),
    ),
  ),
}

/**
 * The image action a stored run performed, whichever provider produced it.
 * @param {string|undefined} providerModelId
 * @returns {string|undefined} One of IMAGE_ACTION, or undefined for an unknown model
 */
export const imageActionOf = providerModelId => ACTION_BY_MODEL_ID[providerModelId]
